import { c as _c } from "react/compiler-runtime";
import { Check, SquareArrowOutUpRight, MoreHorizontal } from "lucide-react";
import Link from "next/link";
import { CardActions } from "@/src/components/cards/card-actions";
import { ComparisonField } from "@/src/components/cards/comparison-field";
import { CopyableContent } from "@/src/components/copyable-content";
import { Badge } from "@/src/components/shadcn/ui/badge";
import { Button } from "@/src/components/shadcn/ui/button";
import { DropdownMenu, DropdownMenuTrigger } from "@/src/components/shadcn/ui/dropdown-menu";
import { ValerieCardColSpan, ValerieCardField, ValerieCardSection, ValerieCardSections } from "@/src/components/valerie-card";
import { getNestedValue } from "@/src/lib/utils/object-utils";
import { compareStrings, getDisplayValue, normalizeForComparison, FieldType } from "@/src/lib/utils/string-utils";
import { PERSON_DEMOGRAPHIC_PLACEHOLDER_VALUES } from "@/src/models/person";
export interface Field {
  key: string;
  label: string;
  type?: FieldType;
  comparable?: boolean;
  required?: boolean;
  colSpan?: ValerieCardColSpan;
  startsRow?: boolean;
  section?: string;
  hideIfEmpty?: boolean;
  // Cards supply callbacks for different model types through this shared field descriptor.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getValue?: (obj: any) => string | null | undefined;
}
function resolveFieldValue(field: Field, obj: unknown): string | null | undefined {
  if (field.getValue) {
    return field.getValue(obj);
  }
  return getNestedValue(obj, field.key);
}
function isEmptyDisplayValue(displayValue: string) {
  return !displayValue.trim() || displayValue === "N/A" || PERSON_DEMOGRAPHIC_PLACEHOLDER_VALUES.has(displayValue);
}
function hasFieldValue(field: Field, obj: unknown) {
  const value = resolveFieldValue(field, obj);
  return !!value && !isEmptyDisplayValue(getDisplayValue(value, field.type ?? "text"));
}
export interface FieldDisplayProps {
  field: Field;
  primaryValue: string | null;
  secondaryValue?: string | null;
  isCompareMode: boolean;
  compareAgainstTitle?: string;
}
export function FieldDisplay(t0) {
  const $ = _c(14);
  const {
    field,
    primaryValue,
    secondaryValue,
    isCompareMode,
    compareAgainstTitle: t1
  } = t0;
  const compareAgainstTitle = t1 === undefined ? "EMR" : t1;
  if (isCompareMode && secondaryValue !== null && field.comparable) {
    const t2 = primaryValue || "N/A";
    const t3 = secondaryValue || "N/A";
    const t4 = field.type ?? "text";
    let t5;
    if ($[0] !== compareAgainstTitle || $[1] !== field.colSpan || $[2] !== field.label || $[3] !== field.startsRow || $[4] !== t2 || $[5] !== t3 || $[6] !== t4) {
      t5 = <ComparisonField label={field.label} colSpan={field.colSpan} startsRow={field.startsRow} value={t2} secondaryValue={t3} type={t4} compareAgainstTitle={compareAgainstTitle} />;
      $[0] = compareAgainstTitle;
      $[1] = field.colSpan;
      $[2] = field.label;
      $[3] = field.startsRow;
      $[4] = t2;
      $[5] = t3;
      $[6] = t4;
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    return t5;
  }
  let t2;
  if ($[8] !== field.colSpan || $[9] !== field.label || $[10] !== field.startsRow || $[11] !== field.type || $[12] !== primaryValue) {
    const displayValue = getDisplayValue(primaryValue, field.type ?? "text");
    t2 = <ValerieCardField label={field.label} colSpan={field.colSpan} startsRow={field.startsRow}>{isEmptyDisplayValue(displayValue) ? <span className="text-sm text-muted-foreground/60">—</span> : <CopyableContent text={displayValue} />}</ValerieCardField>;
    $[8] = field.colSpan;
    $[9] = field.label;
    $[10] = field.startsRow;
    $[11] = field.type;
    $[12] = primaryValue;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  return t2;
}
function groupFieldsBySection(fields: Field[]) {
  const groups: {
    section?: string;
    fields: Field[];
  }[] = [];
  for (const field of fields) {
    const current = groups.at(-1);
    if (current && current.section === field.section) {
      current.fields.push(field);
    } else {
      groups.push({
        section: field.section,
        fields: [field]
      });
    }
  }
  return groups;
}
export function FieldLayout(t0) {
  const $ = _c(26);
  const {
    fields,
    patient,
    secondaryObject,
    compareMode: t1,
    compareAgainstTitle: t2,
    emptyMessage: t3,
    hideEmptyFields: t4
  } = t0;
  const compareMode = t1 === undefined ? false : t1;
  const compareAgainstTitle = t2 === undefined ? "EMR" : t2;
  const emptyMessage = t3 === undefined ? "No details available" : t3;
  const hideEmptyFields = t4 === undefined ? true : t4;
  const comparing = compareMode && !!secondaryObject;
  let T0;
  let t5;
  let t6;
  if ($[0] !== compareAgainstTitle || $[1] !== compareMode || $[2] !== comparing || $[3] !== emptyMessage || $[4] !== fields || $[5] !== hideEmptyFields || $[6] !== patient || $[7] !== secondaryObject) {
    t6 = Symbol.for("react.early_return_sentinel");
    bb0: {
      let t7;
      if ($[11] !== comparing || $[12] !== hideEmptyFields || $[13] !== patient || $[14] !== secondaryObject) {
        t7 = field => !hideEmptyFields && !field.hideIfEmpty || field.required || hasFieldValue(field, patient) || comparing && field.comparable && hasFieldValue(field, secondaryObject);
        $[11] = comparing;
        $[12] = hideEmptyFields;
        $[13] = patient;
        $[14] = secondaryObject;
        $[15] = t7;
      } else {
        t7 = $[15];
      }
      const visibleFields = fields.filter(t7);
      if (visibleFields.length === 0) {
        let t8;
        if ($[16] !== emptyMessage) {
          t8 = emptyMessage ? <p className="text-sm text-muted-foreground">{emptyMessage}</p> : null;
          $[16] = emptyMessage;
          $[17] = t8;
        } else {
          t8 = $[17];
        }
        t6 = t8;
        break bb0;
      }
      T0 = ValerieCardSections;
      let t8;
      if ($[18] !== compareAgainstTitle || $[19] !== compareMode || $[20] !== patient || $[21] !== secondaryObject) {
        t8 = group => <ValerieCardSection key={`${group.section ?? ""}:${group.fields[0].key}`} title={group.section}>{group.fields.map(field_0 => <FieldDisplay key={field_0.key} field={field_0} primaryValue={resolveFieldValue(field_0, patient) || "N/A"} secondaryValue={secondaryObject ? resolveFieldValue(field_0, secondaryObject) || "N/A" : null} isCompareMode={compareMode} compareAgainstTitle={compareAgainstTitle} />)}</ValerieCardSection>;
        $[18] = compareAgainstTitle;
        $[19] = compareMode;
        $[20] = patient;
        $[21] = secondaryObject;
        $[22] = t8;
      } else {
        t8 = $[22];
      }
      t5 = groupFieldsBySection(visibleFields).map(t8);
    }
    $[0] = compareAgainstTitle;
    $[1] = compareMode;
    $[2] = comparing;
    $[3] = emptyMessage;
    $[4] = fields;
    $[5] = hideEmptyFields;
    $[6] = patient;
    $[7] = secondaryObject;
    $[8] = T0;
    $[9] = t5;
    $[10] = t6;
  } else {
    T0 = $[8];
    t5 = $[9];
    t6 = $[10];
  }
  if (t6 !== Symbol.for("react.early_return_sentinel")) {
    return t6;
  }
  let t7;
  if ($[23] !== T0 || $[24] !== t5) {
    t7 = <T0>{t5}</T0>;
    $[23] = T0;
    $[24] = t5;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  return t7;
}
export function hasConflicts(fields: Field[], obj: unknown, secondaryObj: unknown) {
  const comparableFields = fields.filter(field => field.comparable);
  return comparableFields.some(field => {
    const fieldType = field.type ?? "text";
    const primaryValue = normalizeForComparison(getNestedValue(obj, field.key) || "", fieldType);
    const secondaryValue = normalizeForComparison(getNestedValue(secondaryObj, field.key) || "", fieldType);
    return !compareStrings(primaryValue, secondaryValue, fieldType);
  });
}
export function renderBadge(compareMode: boolean, secondaryObject: unknown, hasAnyConflicts: boolean) {
  if (!compareMode) {
    return null;
  }
  if (!secondaryObject) {
    return <MissingDataBadge />;
  }
  if (hasAnyConflicts) {
    return <ConflictsBadge />;
  }
  return <SyncedBadge />;
}
export function SyncedBadge() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Badge variant="outline" className="text-success border-success/30 bg-success/10"><Check className="h-3 w-3 mr-1" />Synced</Badge>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
export function ConflictsBadge() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Badge variant="outline" className="text-warn border-warn/30 bg-warn/10">Conflicts</Badge>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
export function EhrDataBadge() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Badge variant="outline" className="bg-ehr/10 text-ehr">EHR Data</Badge>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
export function MissingDataBadge() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Badge variant="outline" className="text-destructive border-destructive/30 bg-destructive/10">Missing EHR Data</Badge>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
export function LinkToEntity(t0) {
  const $ = _c(3);
  const {
    href
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <SquareArrowOutUpRight className="size-4" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== href) {
    t2 = <Link className="text-muted-foreground hover:text-foreground" href={href} target="_blank" rel="noopener noreferrer">{t1}</Link>;
    $[1] = href;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
export function EntityDropdownMenu(t0) {
  const $ = _c(11);
  const {
    cardActionsProps,
    customProps: t1
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const customProps = t2;
  let t3;
  if ($[2] !== cardActionsProps.customProps) {
    t3 = cardActionsProps.customProps ?? [];
    $[2] = cardActionsProps.customProps;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== customProps || $[5] !== t3) {
    t4 = [...t3, ...customProps];
    $[4] = customProps;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const mergedCustomProps = t4;
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <DropdownMenuTrigger asChild={true}><Button variant="ghost" className="flex h-7 w-7 p-0 text-muted-foreground hover:text-foreground data-[state=open]:bg-muted data-[state=open]:text-foreground"><MoreHorizontal className="h-4 w-4" /><span className="sr-only">Open menu</span></Button></DropdownMenuTrigger>;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== cardActionsProps || $[9] !== mergedCustomProps) {
    t6 = <DropdownMenu>{t5}<CardActions {...cardActionsProps} isContextMenu={false} customProps={mergedCustomProps} /></DropdownMenu>;
    $[8] = cardActionsProps;
    $[9] = mergedCustomProps;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
}